/**
 * SKI AXIS · /refer — Bring Your Cohort referral dashboard.
 *
 * A founder enters their email, the page calls POST /api/cohort/referrals/my-referrals
 * and shows their unique referral link, total credit earned, and per-referral status.
 *
 * AED 500 is credited to the referrer's fee for every accepted Cohort 1 referral.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Copy, CheckCircle2, Clock, ArrowRight, Loader2, AlertCircle,
  Users, Award,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.refer-root{background:#FAF7F0;min-height:100vh;color:#0D1B2A;font-family:'DM Sans',-apple-system,sans-serif;}
.refer-shell{max-width:980px;margin:0 auto;padding:48px 28px 96px;}
@media(max-width:680px){.refer-shell{padding:32px 18px 64px;}}

.refer-hero{padding:48px 0 32px;border-bottom:1px solid rgba(13,27,42,.08);margin-bottom:40px;}
.refer-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:18px;}
.refer-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.refer-h1{font-family:'Cormorant Display',serif;font-size:clamp(40px,5.5vw,62px);font-weight:700;line-height:1.02;letter-spacing:-.02em;margin:0 0 14px;color:#0D1B2A;}
.refer-h1 em{font-style:italic;color:#C8A84B;}
.refer-sub{font-size:17px;line-height:1.55;color:#3D4A5C;max-width:680px;margin:0;font-weight:300;}

/* HOW IT WORKS */
.refer-how{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin:24px 0 48px;}
@media(max-width:760px){.refer-how{grid-template-columns:1fr;}}
.refer-how-card{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:24px 26px;}
.refer-how-num{font-family:'Cormorant Display',serif;font-style:italic;font-size:24px;color:#C8A84B;line-height:1;font-weight:700;margin-bottom:8px;}
.refer-how-t{font-family:'Cormorant Display',serif;font-size:20px;font-weight:700;color:#0D1B2A;margin:0 0 6px;letter-spacing:-.005em;}
.refer-how-d{font-size:13px;color:#3D4A5C;line-height:1.55;margin:0;}

/* LOOKUP FORM */
.refer-lookup{background:#fff;border:1px solid #E8DFC9;padding:36px 40px;display:grid;grid-template-columns:1fr auto;gap:14px;align-items:end;}
@media(max-width:680px){.refer-lookup{grid-template-columns:1fr;padding:24px 22px;}}
.refer-lookup-fld{display:flex;flex-direction:column;gap:6px;}
.refer-lbl{font-size:11px;font-weight:700;letter-spacing:.2em;color:#3D4A5C;text-transform:uppercase;}
.refer-input{font-family:inherit;font-size:14px;color:#0D1B2A;background:#FAF7F0;border:1px solid #E8DFC9;padding:13px 14px;outline:none;}
.refer-input:focus{border-color:#C8A84B;background:#fff;}
.refer-cta{background:#0D1B2A;color:#C8A84B;border:none;padding:14px 26px;font-family:inherit;font-size:11px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;cursor:pointer;display:inline-flex;align-items:center;gap:8px;justify-content:center;transition:all .2s;}
.refer-cta:hover:not(:disabled){background:#C8A84B;color:#0D1B2A;}
.refer-cta:disabled{opacity:.55;cursor:not-allowed;}
.refer-err{margin-top:14px;background:rgba(173,98,64,.08);border-left:3px solid #ad6240;color:#ad6240;padding:12px 16px;font-size:13px;display:flex;align-items:center;gap:10px;}
.refer-err.empty{background:rgba(13,27,42,.04);border-left-color:#7A8190;color:#3D4A5C;}

/* DASHBOARD */
.refer-dash{margin-top:40px;}
.refer-dash-top{background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:48px 56px;border-left:4px solid #C8A84B;display:grid;grid-template-columns:1.4fr 1fr;gap:36px;align-items:center;position:relative;overflow:hidden;}
@media(max-width:760px){.refer-dash-top{grid-template-columns:1fr;padding:32px 28px;gap:24px;}}
.refer-dash-top::before{content:'';position:absolute;top:-60px;right:-60px;width:240px;height:240px;background:radial-gradient(circle,rgba(200,168,75,.15) 0%,transparent 70%);}
.refer-dash-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;margin-bottom:14px;}
.refer-dash-name{font-family:'Cormorant Display',serif;font-size:32px;font-weight:700;line-height:1.1;color:#FAF7F0;margin:0 0 22px;letter-spacing:-.012em;}
.refer-dash-name em{font-style:italic;color:#C8A84B;}
.refer-link-box{background:rgba(250,247,240,.06);border:1px dashed rgba(200,168,75,.5);padding:6px 6px 6px 14px;display:flex;align-items:center;gap:10px;}
.refer-link-box code{flex:1;font-family:'JetBrains Mono','Courier New',monospace;font-size:12.5px;color:#FAF7F0;letter-spacing:.01em;overflow-x:auto;white-space:nowrap;}
.refer-link-copy{background:#C8A84B;color:#0D1B2A;border:none;padding:10px 16px;font-family:inherit;font-size:10.5px;font-weight:800;letter-spacing:.16em;text-transform:uppercase;cursor:pointer;flex-shrink:0;display:inline-flex;align-items:center;gap:6px;transition:all .15s;}
.refer-link-copy:hover{background:#FAF7F0;}
.refer-link-copy.copied{background:#FAF7F0;color:#0D1B2A;}
.refer-credit-box{text-align:center;border-left:1px solid rgba(200,168,75,.2);padding-left:36px;}
@media(max-width:760px){.refer-credit-box{border-left:0;border-top:1px solid rgba(200,168,75,.2);padding:22px 0 0;}}
.refer-credit-eb{font-size:10.5px;font-weight:700;letter-spacing:.28em;color:#C8A84B;text-transform:uppercase;margin-bottom:8px;}
.refer-credit-num{font-family:'Cormorant Display',serif;font-size:64px;font-weight:700;color:#FAF7F0;line-height:1;letter-spacing:-.02em;margin:0;}
.refer-credit-num em{color:#C8A84B;font-style:italic;font-size:32px;font-weight:600;}
.refer-credit-foot{font-size:12px;color:rgba(250,247,240,.6);letter-spacing:.04em;margin-top:8px;font-style:italic;font-family:'Cormorant Display',serif;font-size:14px;}

/* REFERRALS TABLE */
.refer-list{margin-top:32px;background:#fff;border:1px solid #E8DFC9;}
.refer-list-h{padding:22px 28px 16px;border-bottom:1px solid #E8DFC9;display:flex;justify-content:space-between;align-items:baseline;flex-wrap:wrap;gap:8px;}
.refer-list-h h3{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;color:#0D1B2A;margin:0;letter-spacing:-.008em;}
.refer-list-h h3 em{font-style:italic;color:#C8A84B;}
.refer-list-meta{font-size:12px;color:#7A8190;letter-spacing:.04em;font-style:italic;font-family:'Cormorant Display',serif;font-size:14px;}
.refer-row{display:grid;grid-template-columns:1.4fr 1fr 110px 110px;gap:18px;padding:18px 28px;border-bottom:1px solid #F0EBDD;align-items:center;font-size:13.5px;}
@media(max-width:680px){.refer-row{grid-template-columns:1fr;gap:6px;padding:16px 20px;}}
.refer-row:last-child{border-bottom:0;}
.refer-row-name{font-family:'Cormorant Display',serif;font-size:18px;font-weight:700;color:#0D1B2A;letter-spacing:-.005em;}
.refer-row-co{color:#3D4A5C;font-size:13px;}
.refer-row-status{font-size:10.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;padding:6px 12px;border:1px solid #E8DFC9;display:inline-flex;align-items:center;gap:6px;width:fit-content;}
.refer-row-status.accepted{color:#C8A84B;border-color:#C8A84B;background:rgba(200,168,75,.06);}
.refer-row-status.pending{color:#7A8190;}
.refer-row-credit{font-family:'Cormorant Display',serif;font-size:18px;font-weight:700;color:#C8A84B;letter-spacing:-.01em;text-align:right;}
.refer-row-credit.zero{color:#7A8190;font-style:italic;}

.refer-empty{padding:48px 28px;text-align:center;color:#7A8190;font-size:14px;font-style:italic;font-family:'Cormorant Display',serif;font-size:18px;line-height:1.55;}
`;

export default function ReferPage() {
  useSEO({
    title: 'Bring Your Cohort · SKI Axis Referral Programme',
    description: 'Track your SKI Axis Cohort 1 referrals. Earn AED 500 off your programme fee for every founder you refer who is accepted. No cap.',
    noindex: true,
  });

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const onLookup = async (e) => {
    e.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const { data: resp } = await axios.post(`${API}/cohort/referrals/my-referrals`, { email });
      if (!resp.found) {
        setError("We don't have a Cohort 1 application on file for this email. Apply first to get your referral link.");
        setData(null);
      } else {
        setData(resp);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refLink = data?.referral_code
    ? `${typeof window !== 'undefined' ? window.location.origin : 'https://skiaxis.com'}/apply?ref=${data.referral_code}`
    : '';

  const onCopy = () => {
    if (!refLink) return;
    navigator.clipboard?.writeText(refLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="refer-root" data-testid="refer-page">
      <style>{STYLES}</style>
      <Header />

      <div className="refer-shell">
        <div className="refer-hero">
          <div className="refer-eb">Bring Your Cohort · AED 500 / accepted referral</div>
          <h1 className="refer-h1">Refer founders. <em>Earn AED 500</em> per acceptance.</h1>
          <p className="refer-sub">
            For every founder you bring to SKI Axis Cohort 1 who is accepted, AED 500 is credited toward your own fee.
            No cap. Track your referrals below.
          </p>
        </div>

        {/* HOW IT WORKS */}
        <div className="refer-how">
          <div className="refer-how-card">
            <div className="refer-how-num">i.</div>
            <h3 className="refer-how-t">Apply</h3>
            <p className="refer-how-d">Submit your own Cohort 1 application — every applicant gets a unique referral code.</p>
          </div>
          <div className="refer-how-card">
            <div className="refer-how-num">ii.</div>
            <h3 className="refer-how-t">Share</h3>
            <p className="refer-how-d">Send your link to founders worth admitting — the link auto-attaches your code on their application.</p>
          </div>
          <div className="refer-how-card">
            <div className="refer-how-num">iii.</div>
            <h3 className="refer-how-t">Earn</h3>
            <p className="refer-how-d">When they're accepted, AED 500 is credited toward your fee. Refer five — bring it down by AED 2,500.</p>
          </div>
        </div>

        {/* LOOKUP FORM */}
        {!data && (
          <form onSubmit={onLookup} className="refer-lookup" data-testid="refer-lookup-form">
            <div className="refer-lookup-fld">
              <label className="refer-lbl">Your application email</label>
              <input
                type="email"
                className="refer-input"
                placeholder="founder@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="refer-email-input"
                required
              />
            </div>
            <button type="submit" className="refer-cta" disabled={loading} data-testid="refer-lookup-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {loading ? 'Looking up…' : 'View my referrals'}
            </button>
          </form>
        )}
        {error && (
          <div className={`refer-err ${error.includes("don't have") ? 'empty' : ''}`} data-testid="refer-error">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
            {error.includes("don't have") && (
              <Link to="/apply" style={{ color: '#C8A84B', fontWeight: 700, marginLeft: 8, textDecoration: 'none' }}>
                Apply now →
              </Link>
            )}
          </div>
        )}

        {/* DASHBOARD */}
        {data && (
          <div className="refer-dash" data-testid="refer-dashboard">
            <div className="refer-dash-top">
              <div>
                <div className="refer-dash-eb">Your referral link</div>
                <h2 className="refer-dash-name">Welcome, <em>{(data.founder_name || '').split(' ')[0] || 'Founder'}</em>.</h2>
                <div className="refer-link-box">
                  <code data-testid="refer-link">{refLink}</code>
                  <button
                    type="button"
                    className={`refer-link-copy ${copied ? 'copied' : ''}`}
                    onClick={onCopy}
                    data-testid="refer-copy-btn"
                  >
                    {copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <div className="refer-credit-box">
                <div className="refer-credit-eb">Total credit earned</div>
                <div className="refer-credit-num" data-testid="refer-credit-total">
                  <em>AED</em> {Number(data.credit_earned_aed || 0).toLocaleString('en-GB')}
                </div>
                <div className="refer-credit-foot">AED {data.credit_per_referral_aed || 500} per accepted referral</div>
              </div>
            </div>

            {/* REFERRALS LIST */}
            <div className="refer-list" data-testid="refer-list">
              <div className="refer-list-h">
                <h3>Your <em>referrals</em></h3>
                <div className="refer-list-meta">{data.referrals?.length || 0} {data.referrals?.length === 1 ? 'referral' : 'referrals'}</div>
              </div>
              {(!data.referrals || data.referrals.length === 0) ? (
                <div className="refer-empty" data-testid="refer-empty">
                  No referrals yet. Share your link above — every accepted founder credits AED 500 toward your fee.
                </div>
              ) : (
                data.referrals.map((r) => (
                  <div key={r.id} className="refer-row" data-testid={`refer-row-${r.id}`}>
                    <div>
                      <div className="refer-row-name">{r.name || '—'}</div>
                      <div className="refer-row-co">{r.company || ''}</div>
                    </div>
                    <div style={{ fontSize: 12, color: '#7A8190', letterSpacing: '.04em' }}>
                      {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                    </div>
                    <div>
                      {r.credited ? (
                        <span className="refer-row-status accepted">
                          <CheckCircle2 className="h-3 w-3" /> Accepted
                        </span>
                      ) : (
                        <span className="refer-row-status pending">
                          <Clock className="h-3 w-3" /> {r.status === 'rejected' ? 'Closed' : 'In review'}
                        </span>
                      )}
                    </div>
                    <div className={`refer-row-credit ${r.credited ? '' : 'zero'}`}>
                      {r.credited ? `+ AED ${data.credit_per_referral_aed || 500}` : '— '}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={{ marginTop: 32, textAlign: 'center' }}>
              <Link to="/apply" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: '#0D1B2A', color: '#C8A84B', padding: '14px 28px', fontSize: 11, fontWeight: 800, letterSpacing: '.18em', textTransform: 'uppercase', textDecoration: 'none' }} data-testid="refer-share-on-apply">
                <Users className="h-4 w-4" /> Share with another founder <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        )}

      </div>

      <Footer />
    </div>
  );
}
